import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000ResponsiveToolbar from '../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { FaPlus, FaRegFile, FaTrashAlt } from 'react-icons/fa';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import { NS, U3203ROLES } from '../../../U3203';
import type { U3203AiMissionFormRow } from '../../../U3203Service';
import { u2000CanManageScopeAwareEntity, u2000CanModifyU3203Mission } from '../../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3203AiMissionFormToolBarProps {
  formStateHandlers: U2000FormStateHandlers<U3203AiMissionFormRow> & {
    getData: () => U3203AiMissionFormRow | null;
    getFormState: () => U2000FormStateEnumState;
    addNewMission: () => void;
    modifyMission: () => void;
    deleteMission: () => void;
  };
}

export const U3203AiMissionFormToolBar: React.FC<U3203AiMissionFormToolBarProps> = ({ formStateHandlers }) => {
  const formState = formStateHandlers.getFormState();
  const data = formStateHandlers.getData();
  const { auth, hasRole } = useU2000AuthContext();
  const { t } = useTranslation();
  const canModifyMission = u2000CanModifyU3203Mission({
    isSystemScope: data?.systemScope === true,
    hasEditRole: hasRole(U3203ROLES.EDIT),
    sessionOrganizationId: auth.user.organizationId,
  });
  const canDeleteMission = u2000CanManageScopeAwareEntity({
    isSystemScope: data?.systemScope === true,
    hasRequiredRole: hasRole(U3203ROLES.DELETE),
    sessionOrganizationId: auth.user.organizationId,
  });

  const actions = [
    {
      key: 'add',
      label: t('btnAdd', { ns: NS }),
      hasRole: hasRole(U3203ROLES.ADD),
      handleClick: () => {
        formStateHandlers.addNewMission();
      },
      disabled: formState === U2000FormStateEnumState.EDIT,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: 'modify',
      label: t('btnModify', { ns: NS }),
      hasRole: hasRole(U3203ROLES.EDIT),
      handleClick: () => {
        formStateHandlers.modifyMission();
      },
      disabled: formState === U2000FormStateEnumState.EDIT || !canModifyMission,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: 'delete',
      label: t('btnDelete', { ns: NS }),
      hasRole: hasRole(U3203ROLES.DELETE),
      handleClick: () => {
        formStateHandlers.deleteMission();
      },
      disabled: formState === U2000FormStateEnumState.EDIT || !canDeleteMission,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
